import { supabase } from "@/integrations/supabase/client";

/**
 * Checks if the user stayed under budget for the previous month.
 * If so, appends the month string (e.g. "2026-01") to months_under_budget in user_progress.
 */
export async function populateMonthsUnderBudget(userId: string): Promise<void> {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const firstOfPrevMonth = prevMonth.toISOString().split('T')[0];
  const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const monthKey = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`;

  // Fetch budget + expenses + current months_under_budget in parallel
  const [budgetResult, expensesResult, progressResult] = await Promise.all([
    supabase
      .from('budget_settings')
      .select('monthly_budget')
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('expenses')
      .select('amount')
      .eq('user_id', userId)
      .gte('expense_date', firstOfPrevMonth)
      .lt('expense_date', firstOfThisMonth),
    supabase
      .from('user_progress')
      .select('months_under_budget')
      .eq('user_id', userId)
      .maybeSingle(),
  ]);

  const monthlyBudget = budgetResult.data?.monthly_budget;
  if (!monthlyBudget) return; // No budget set

  const totalSpent = expensesResult.data?.reduce((sum, e) => sum + Number(e.amount), 0) ?? 0;
  const existingMonths = (progressResult.data?.months_under_budget as string[]) ?? [];

  // Already recorded or over budget
  if (existingMonths.includes(monthKey)) return;
  if (totalSpent > monthlyBudget) return;

  // Append month to array
  const updatedMonths = [...existingMonths, monthKey];
  await supabase
    .from('user_progress')
    .update({ months_under_budget: updatedMonths })
    .eq('user_id', userId);
}
